import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { BADGE_ICON } from '../Competitions/BadgeShelf';
import './WallOfFame.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const PER_PAGE = 3; // 3 people at a time
const EVERY_MS = 5000; // next 3 every 5 seconds
const WIDE_PX = 640; // wider than this: the 3 side by side, else one under the other

const ago = (v) => {
  const d = new Date(v);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (Number.isNaN(days)) return '';
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Face = ({ x }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmwf-face">
      {x.photo && !broken ? <img src={x.photo} alt="" loading="lazy" onError={() => setBroken(true)} /> : <span>{String(x.name || '?').replace('Teacher ', '').charAt(0)}</span>}
      <i className={`rmwf-badge is-${x.badge}`} aria-hidden="true">
        <FontAwesomeIcon icon={BADGE_ICON[x.icon] || faAward} />
      </i>
    </span>
  );
};

/* "New at RoboMind" (teacher dashboard): a paid student who just joined, with their teacher */
const Joined = ({ x }) => (
  <li className={x.mine ? 'is-mine' : ''}>
    <Face x={x} />
    <div className="rmwf-text">
      <p className="rmwf-who">
        <b>{x.name}</b> joined the academy{x.teacher ? <> with <strong>{x.mine ? 'you' : `Teacher ${x.teacher}`}</strong></> : ''}
      </p>
      <small>{[x.mine ? 'Congrats! 🎉' : 'Congrats 👏', ago(x.at)].join(' · ')}</small>
    </div>
  </li>
);

const Person = ({ x }) => x.kind === 'joined' ? <Joined x={x} /> : (
  <li className={x.mine ? 'is-mine' : ''}>
    <Face x={x} />
    <div className="rmwf-text">
      <p className="rmwf-who">
        <b>{x.mine ? 'You' : x.name}</b> earned <strong>{x.title}</strong>
      </p>
      {x.note && <p className="rmwf-note">“{x.note}”</p>}
      <small>
        {x.mine ? 'Well done! 🌟 · ' : ''}
        {[x.period, x.nominatedBy ? `by Teacher ${x.nominatedBy}` : '', ago(x.at)].filter(Boolean).join(' · ')}
      </small>
    </div>
  </li>
);

/* "Wall of fame": who earned a badge or a medal this month and last month (students and teachers).
   Shows 3 at a time and moves to the next 3 by itself (pauses on hover / focus).
   path: '/api/student/awards/feed' or '/api/teacher/awards/feed'.
   items: already loaded by the page (free-trial dashboard, home page). */
const WallOfFame = ({ path, className = '', empty, items, title = 'Wall of fame', emoji = '🎉' }) => {
  const [loaded, setFeed] = useState(null);
  const feed = items || loaded;
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const [wide, setWide] = useState(true);
  const boxRef = useRef(null);

  useEffect(() => {
    if (items || !path) return undefined;
    let cancelled = false;
    axios
      .get(`${API}${path}`, auth())
      .then((res) => !cancelled && setFeed(res.data?.feed || []))
      .catch(() => !cancelled && setFeed([]));
    return () => {
      cancelled = true;
    };
  }, [path, items]);

  // 3 side by side when there is room, else one under the other
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setWide(entry.contentRect.width >= WIDE_PX));
    ro.observe(el);
    return () => ro.disconnect();
  }, [feed]);

  const pages = [];
  (feed || []).forEach((x, i) => {
    if (i % PER_PAGE === 0) pages.push([]);
    pages[pages.length - 1].push(x);
  });
  const count = pages.length;

  const go = useCallback((n) => setPage(((n % count) + count) % count), [count]);

  useEffect(() => {
    if (page >= count && count) setPage(0);
  }, [page, count]);

  useEffect(() => {
    if (count < 2 || paused || reducedMotion()) return undefined;
    const t = setInterval(() => setPage((p) => (p + 1) % count), EVERY_MS);
    return () => clearInterval(t);
  }, [count, paused]);

  if (!feed) return null;

  return (
    <section
      ref={boxRef}
      className={`rmwf ${wide ? 'is-wide' : 'is-narrow'} ${className}`}
      aria-roledescription="carousel"
      aria-label={title}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="rmwf-head">
        <h2 className="rmwf-title">
          <span aria-hidden="true">{emoji}</span> {title}
        </h2>
        {count > 1 && (
          <div className="rmwf-nav">
            <button type="button" onClick={() => go(page - 1)} aria-label="Previous">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button type="button" onClick={() => go(page + 1)} aria-label="Next">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {feed.length === 0 ? (
        <p className="rmwf-empty">
          {empty || 'No badges yet this month. Join the class on time, work hard and send a project to a challenge: your name could be here first! 🌟'}
        </p>
      ) : (
        <div className="rmwf-viewport">
          <div className="rmwf-track" style={{ transform: `translateX(-${page * 100}%)` }}>
            {pages.map((list, i) => (
              <ul key={i} className="rmwf-page" aria-hidden={i !== page} aria-label={`${i + 1} of ${count}`}>
                {list.map((x) => (
                  <Person key={x.id} x={x} />
                ))}
              </ul>
            ))}
          </div>
        </div>
      )}

      {count > 1 && (
        <div className="rmwf-dots" role="tablist" aria-label="Pages">
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === page}
              aria-label={`Page ${i + 1}`}
              className={i === page ? 'is-on' : ''}
              onClick={() => go(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export { Face, ago };
export default WallOfFame;
